// ─── Program ladder ──────────────────────────────────────────────────────────
// Where each program sits in a ladder of stacked programs, and the whole ladder
// laid out as one grid — the view a seminary keeps in a spreadsheet: SEBEX's
// Certificado in year 1, Predicación Expositiva in years 2-3, Ministerio Pastoral
// in years 4-5, and the Maestría spanning all six.
//
// Nothing here is stored. The only fact on record is `stacksOnProgramId` — "this
// program builds on that one" — and every position is computed from it, so a
// start year can never disagree with the plans it sits on.
import { curriculumVersionStatus, } from '../entities/academic.entity.js';
/**
 * The catalog version a ladder shows for a program: the current catalog, else
 * the first active version, else the first one. The same pick the program
 * screen makes for its malla. Named fallbacks, because a program whose current
 * catalog was never set is common and must still land somewhere visible.
 */
export function resolveLadderVersion(program) {
    const versions = program.curriculumVersions ?? [];
    return (versions.find((v) => v.catalogYear === program.currentCatalogYear) ??
        versions.find((v) => curriculumVersionStatus(v) === 'active') ??
        versions[0]);
}
/**
 * How many ladder periods a program occupies: the span of its plan's terms.
 * FALLBACK — a program with no plan loaded occupies `durationTerms` (at least
 * one), so the programs built on it still start after it instead of on top of it.
 */
export function programTermCount(program) {
    const terms = resolveLadderVersion(program)?.terms ?? [];
    if (terms.length > 0)
        return Math.max(...terms.map((t) => t.termOffset)) + 1;
    return Math.max(1, program.durationTerms ?? 1);
}
/**
 * The chain that `candidateId` would close into a loop if `programId` built on
 * it — `[programId, candidateId, …, programId]` — or null when it would not.
 * Building on itself is the shortest loop.
 */
export function findStackCycle(programs, programId, candidateId) {
    const byId = new Map(programs.map((p) => [p.id, p]));
    const chain = [programId];
    let current = candidateId;
    const seen = new Set();
    while (current !== undefined) {
        chain.push(current);
        if (current === programId)
            return chain;
        if (seen.has(current))
            return null; // a loop elsewhere, not through programId
        seen.add(current);
        current = byId.get(current)?.stacksOnProgramId;
    }
    return null;
}
/**
 * The programs below `programId` on its ladder, base first, NOT including it:
 * for SEBEX's Maestría, [Certificado, Predicación Expositiva, Ministerio
 * Pastoral]. Stops at a predecessor that does not exist and at a loop, so a
 * broken edge yields a shorter chain instead of an endless one.
 */
export function stackChain(programs, programId) {
    const byId = new Map(programs.map((p) => [p.id, p]));
    const below = [];
    const seen = new Set([programId]);
    let current = byId.get(programId)?.stacksOnProgramId;
    while (current !== undefined && !seen.has(current)) {
        const program = byId.get(current);
        if (!program)
            break;
        below.unshift(program);
        seen.add(current);
        current = program.stacksOnProgramId;
    }
    return below;
}
/**
 * One plan made of several, laid end to end: each part's terms shifted past the
 * terms of the parts before it. It is what a cumulative award is audited
 * against — the Maestría's year 6 after the Certificado's year 1, Predicación's
 * years 2-3 and Ministerio's years 4-5.
 *
 * A course already required by an earlier part is not required again. Parts
 * with no plan still occupy their `termCount`, so the offsets after them keep
 * matching the ladder.
 */
export function cumulativeCurriculum(parts, catalogYear) {
    const terms = [];
    const required = new Set();
    let shift = 0;
    for (const part of parts) {
        for (const term of [...(part.version?.terms ?? [])].sort((a, b) => a.termOffset - b.termOffset)) {
            const courseIds = term.recommendedCourseIds.filter((id) => !required.has(id));
            courseIds.forEach((id) => required.add(id));
            terms.push({
                termOffset: shift + term.termOffset,
                recommendedCourseIds: courseIds,
                ...(term.label !== undefined ? { label: term.label } : {}),
                ...(term.electiveSlots !== undefined ? { electiveSlots: term.electiveSlots } : {}),
            });
        }
        shift += part.termCount;
    }
    return { catalogYear, terms };
}
/** Whether entering `program` is allowed, given whether its predecessor is done. */
export function evaluateStackEntry(params) {
    const predecessorId = params.program.stacksOnProgramId ?? null;
    return {
        predecessorId,
        enforced: predecessorId !== null && params.program.enforceStackEntry === true,
        satisfied: predecessorId === null || params.predecessorCompleted,
    };
}
/**
 * CÓMO SE LLAMA UNA COLUMNA — una sola regla para la pantalla, el PDF y el
 * aviso de cursos extraviados.
 *
 * Estaba escrita TRES veces (la página y dos sitios del PDF), y al añadir los
 * años habría habido que corregir las tres: el PDF y la pantalla habrían
 * nombrado la misma columna distinto hasta que alguien lo notara. Es el mismo
 * patrón que ya costó caro en este repositorio.
 *
 * EL AÑO MANDA SOBRE LA ETIQUETA. En un cruce las etiquetas no coinciden —el
 * Año 2 del Certificado dice «Semestre 1» y el de Predicación «Semestre 2»— y
 * la columna se quedaba sin nombre. El año es un dato; la etiqueta, cómo lo
 * escribió cada quien.
 *
 * Los textos los pone quien llama, porque el dominio no traduce.
 */
export function ladderColumnLabel(column, index, nombres) {
    if (column?.year !== undefined)
        return nombres.porAnio(column.year);
    return column?.label ?? nombres.porPosicion(index + 1);
}
/**
 * Lays out every program with a plan as rows of a single grid. Archived programs
 * are left out. A program built on one that does not exist (or is archived), or
 * caught in a loop, starts at column 0 and is REPORTED — hiding it would make a
 * broken ladder look complete.
 */
export function buildProgramLadder(allPrograms) {
    const programs = allPrograms.filter((p) => p.status !== 'archived');
    const byId = new Map(programs.map((p) => [p.id, p]));
    const problems = [];
    const start = new Map();
    const depth = new Map();
    const cyclic = new Set();
    // `path` = the programs already being placed below this one in the recursion.
    const place = (id, path) => {
        const known = start.get(id);
        if (known !== undefined)
            return { start: known, depth: depth.get(id) };
        const program = byId.get(id);
        const prevId = program.stacksOnProgramId;
        let result = { start: 0, depth: 0 };
        if (prevId !== undefined) {
            const prev = byId.get(prevId);
            if (!prev) {
                problems.push({ kind: 'missing_predecessor', programId: id, stacksOnProgramId: prevId });
            }
            else if (prevId === id || path.includes(prevId)) {
                const loop = prevId === id ? [id] : [...path.slice(path.indexOf(prevId)), id];
                if (!loop.some((x) => cyclic.has(x)))
                    problems.push({ kind: 'cycle', programIds: loop });
                loop.forEach((x) => cyclic.add(x));
            }
            else {
                const p = place(prevId, [...path, id]);
                if (!cyclic.has(id)) {
                    result = { start: p.start + programTermCount(prev), depth: p.depth + 1 };
                }
            }
        }
        if (cyclic.has(id))
            result = { start: 0, depth: 0 };
        start.set(id, result.start);
        depth.set(id, result.depth);
        return result;
    };
    programs.forEach((p) => place(p.id, []));
    const hasSuccessor = new Set(programs.map((p) => p.stacksOnProgramId).filter((x) => x !== undefined));
    // ─── ¿La escalera está expresada en AÑOS o en turnos apilados? ─────────────
    //
    // APILADO es lo que hizo siempre: cada programa empieza donde termina aquel
    // en el que se apoya. Sirve mientras nadie se cruce, y deja de servir en
    // cuanto dos programas comparten un año — dibuja dos columnas con el mismo
    // nombre, que es lo que pasó en producción el 28-09-2026.
    //
    // POR AÑOS, cuando los planes declaran a qué año pertenece cada término: una
    // columna por año, y los que se cruzan la comparten AUNQUE NO SE APOYEN uno
    // en otro.
    //
    // TODO O NADA, y a propósito. Media escalera en años y media apilada son dos
    // sistemas de coordenadas en la misma grilla: las columnas se verían
    // ordenadas y no significarían lo mismo de una fila a otra. Ante la mezcla se
    // vuelve entero al apilado —el comportamiento de siempre, que nunca miente
    // sobre sí mismo— y se REPORTA cuáles planes faltan.
    const termsOf = (p) => resolveLadderVersion(p)?.terms ?? [];
    const conPlan = programs.filter((p) => termsOf(p).length > 0);
    const declaran = conPlan.filter((p) => termsOf(p).every((t) => typeof t.year === 'number'));
    const enAnios = conPlan.length > 0 && declaran.length === conPlan.length;
    if (declaran.length > 0 && !enAnios) {
        problems.push({
            kind: 'mixed_year_declaration',
            programIds: conPlan.filter((p) => !declaran.includes(p)).map((p) => p.id),
        });
    }
    // Una columna por año distinto, en orden. Los años no tienen por qué ser
    // correlativos: si nadie cursa nada en el Año 3, no hay columna vacía.
    const anios = enAnios
        ? [...new Set(conPlan.flatMap((p) => termsOf(p).map((t) => t.year)))].sort((a, b) => a - b)
        : [];
    const columnaDeAnio = new Map(anios.map((y, i) => [y, i]));
    const rows = programs
        .map((p) => {
        const version = resolveLadderVersion(p);
        const s = start.get(p.id);
        const terms = [...(version?.terms ?? [])]
            // Por año y semestre cuando están declarados, y si no por el turno.
            // Sin este orden, dos términos del mismo año saldrían en el orden en
            // que quedaron guardados y el segundo semestre podría dibujarse antes
            // que el primero.
            .sort((a, b) => (a.year ?? 0) - (b.year ?? 0) ||
            (a.semester ?? 0) - (b.semester ?? 0) ||
            a.termOffset - b.termOffset)
            .map((t) => ({
            column: enAnios ? columnaDeAnio.get(t.year) : s + t.termOffset,
            ...(t.label?.trim() ? { label: t.label.trim() } : {}),
            ...(t.semester !== undefined ? { semester: t.semester } : {}),
            courseIds: [...t.recommendedCourseIds],
        }));
        // CUÁNTAS COLUMNAS OCUPA, no cuántos términos tiene. En el apilado
        // coinciden —un término por columna— pero por años un programa puede
        // poner dos términos en el mismo año y ocupar UNA sola columna. La vista
        // y el PDF pintan el tramo con esto, así que confundirlos pintaría de
        // fondo columnas donde el programa no cursa nada.
        const columnas = terms.map((t) => t.column);
        const desde = columnas.length > 0 ? Math.min(...columnas) : s;
        const hasta = columnas.length > 0 ? Math.max(...columnas) : s;
        return {
            programId: p.id,
            name: p.name,
            startColumn: enAnios ? desde : s,
            termCount: enAnios ? hasta - desde + 1 : programTermCount(p),
            depth: depth.get(p.id),
            includedProgramIds: p.includesStackedPrograms && !cyclic.has(p.id)
                ? stackChain(programs, p.id).map((x) => x.id)
                : [],
            inChain: !cyclic.has(p.id) &&
                ((p.stacksOnProgramId !== undefined && byId.has(p.stacksOnProgramId)) ||
                    hasSuccessor.has(p.id)),
            terms,
        };
    })
        .filter((r) => r.terms.length > 0)
        // Chains first, base to tip; standalone programs after, so one that spans the
        // whole ladder (the Maestría) sits under the rungs it repeats.
        .sort((a, b) => Number(b.inChain) - Number(a.inChain) ||
        a.startColumn - b.startColumn ||
        a.depth - b.depth ||
        a.termCount - b.termCount ||
        a.name.localeCompare(b.name));
    const columnCount = rows.reduce((max, r) => Math.max(max, ...r.terms.map((t) => t.column + 1)), 0);
    const columns = Array.from({ length: columnCount }, (_, index) => {
        const terms = rows.flatMap((r) => r.terms.filter((t) => t.column === index));
        const labels = new Set(terms.map((t) => t.label ?? ''));
        const only = labels.size === 1 ? [...labels][0] : '';
        return {
            index,
            label: only !== '' ? only : null,
            // POR AÑOS, LA ETIQUETA YA NO DECIDE EL NOMBRE. Antes la columna se
            // llamaba como la etiqueta en que todos los planes coincidían, y en un
            // cruce no coinciden: el Año 2 del Certificado y el de Predicación
            // llevan textos distintos y la columna se quedaba sin nombre. El año es
            // un dato, no un texto, y nombrar por él no depende de cómo lo escribió
            // cada quien.
            ...(anios[index] !== undefined ? { year: anios[index] } : {}),
            courseIds: [...new Set(terms.flatMap((t) => t.courseIds))],
        };
    });
    const columnsByCourse = new Map();
    for (const r of rows) {
        for (const t of r.terms) {
            for (const id of t.courseIds) {
                if (!columnsByCourse.has(id))
                    columnsByCourse.set(id, new Set());
                columnsByCourse.get(id).add(t.column);
            }
        }
    }
    const misplaced = [...columnsByCourse]
        .filter(([, cols]) => cols.size > 1)
        .map(([courseId, cols]) => ({ courseId, columns: [...cols].sort((a, b) => a - b) }));
    return { columnCount, columns, rows, misplaced, problems };
}
//# sourceMappingURL=program-ladder.service.js.map